import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import {
  confirmAcknowledgement,
  getMyAcknowledgement,
  getMyAcknowledgements,
  getMyAcknowledgementSummary,
} from '@/api/acknowledgements';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';

export function useMyAcknowledgements(status?: 'pending' | 'acknowledged') {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MyAcknowledgements, user?.id, 'list', status ?? 'all'],
    queryFn: () => getMyAcknowledgements(status),
    enabled: !!user,
    select: (res) => res.data,
  });
}

/** The nav badge: refreshed every minute and on focus. */
export function useMyAcknowledgementsPending() {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MyAcknowledgements, user?.id, 'summary'],
    queryFn: getMyAcknowledgementSummary,
    enabled: !!user,
    refetchInterval: 60_000,
    select: (res) => res.data.pending,
  });
}

export function useMyAcknowledgement(id?: string) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MyAcknowledgements, user?.id, 'one', id],
    queryFn: () => getMyAcknowledgement(id!),
    enabled: !!user && !!id,
    select: (res) => res.data,
  });
}

export function useConfirmAcknowledgement() {
  const { user } = useEmployeePortalAuth();
  const { t } = useTranslation(TranslationNS.Esignature);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      contentSha256,
      answers,
    }: {
      id: string;
      contentSha256: string;
      answers?: Record<string, unknown>;
      /** A form is submitted, not confirmed: the toast says so. */
      isForm?: boolean;
    }) => confirmAcknowledgement(id, contentSha256, answers),
    onSuccess: (_data, { isForm }) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MyAcknowledgements, user?.id],
      });
      toast.success(
        t(isForm ? 'fields.toast_submitted' : 'read.toast_confirmed')
      );
    },
  });
}
